import { useEffect, useState } from 'react';
import moshePhoto from './assets/images/dr-moshe-katan.png';
import phdCertificate from './assets/images/phd-certificate.png';
import ContactForms from './components/ContactForms';
import LogoCarousel from './components/LogoCarousel';
import { fetchVistageGroupCompanies } from './api/vistageGroupCompanies';
import './App.css';
import './vistage-look.css';

const GROUP_TOOLS = {
  purposeNot: 'המטרה היא לא עוד קורס ניהול',
  purposeYes: 'המטרה היא קהילת מנכ״לים שמובילה שינוי וצמיחה',
  lead: 'בקבוצה תקבלו מרחב סודי לשיתוף דילמות, פרספקטיבה של עמיתים, וכלים שאפשר לקחת ישר לחדר הישיבות',
  items: [
    'לשתף דילמות אמיתיות ולקבל פידבק כנה ממנכ״לים בגובה העיניים.',
    'לקבל פרספקטיבה מניסיון של עמיתים שכבר התמודדו עם אתגר דומה.',
    'לצאת מכל מפגש חודשי עם כיוון ברור יותר באסטרטגיה, צמיחה וניהול סיכונים.',
    'לקבל כל שבוע סיכומי מאמרים, הרצאות עם תובנות וכלים פרקטיים.',
    'להפסיק לשאת את התפקיד לבד — ולהיות חלק מקבוצה שמקדמת אתכם.',
  ],
  punch: 'Vistage היא לא עוד קורס ניהול — זו קהילה של מנהיגים עסקיים שמובילים שינוי וצמיחה',
};

const ACADEMY_MEDIA_BASE = 'https://pub-1ee49b423fb0441183f8f72f39897cc7.r2.dev/greatleaders-academy/images';

const INSTRUCTOR_LOGOS = [
  { src: `${ACADEMY_MEDIA_BASE}/poznan-university.png`, alt: 'University of Poznan' },
  { src: `${ACADEMY_MEDIA_BASE}/coca-cola.png`, alt: 'Coca-Cola' },
  { src: `${ACADEMY_MEDIA_BASE}/carlsberg.png`, alt: 'Carlsberg' },
  { src: `${ACADEMY_MEDIA_BASE}/strauss.png`, alt: 'Strauss Elite' },
];

const TAU_LOGOS = [
  { src: `${ACADEMY_MEDIA_BASE}/tau-logo.png`, alt: 'אוניברסיטת תל אביב' },
  { src: `${ACADEMY_MEDIA_BASE}/coller-logo.png`, alt: 'פקולטה לניהול - אוניברסיטת תל אביב' },
];

function OptionalImg({ src, alt, className, wrapClassName }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  const img = (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
  return wrapClassName ? <div className={wrapClassName}>{img}</div> : img;
}

function App() {
  const [groupCompanies, setGroupCompanies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchVistageGroupCompanies()
      .then((items) => {
        if (!cancelled) setGroupCompanies(items);
      })
      .catch(() => {
        if (!cancelled) setGroupCompanies([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const inIframe = window.self !== window.top;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, {
      threshold: 0,
      rootMargin: '0px',
    });

    const animatedElements = document.querySelectorAll(
      '.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale'
    );
    animatedElements.forEach((el) => {
      if (inIframe) el.classList.add('animate-in');
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App vistage-page" dir="rtl">
      <section className="philosophy-section vistage-open scroll-animate">
        <div className="container">
          <div className="philosophy-content vistage-manifesto">
            <p className="philosophy-text">
              אני מזמין אתכם להצטרף לרשת המובילים של <strong>Vistage Israel</strong> — קבוצת מנכ״לים שבה משתפים דילמות אמיתיות,
              מתייעצים, ומקבלים פרספקטיבה מניסיון של עמיתים. <strong>לא עוד ייעוץ מהצד</strong>: שולחן עגול סודי,
              בגובה העיניים, עם מי שנושא את אותו תפקיד.
            </p>
            <div className="vistage-mv">
              <article className="mission-section">
                <h3 className="mission-title">היעוד שלנו:</h3>
                <p className="mission-text">
                  להעניק למנכ״לים פלטפורמה לליווי אישי, הכוונה מקצועית ורשת עמיתים איכותית —
                  שנותנת מענה לאתגרים ולהזדמנויות של התפקיד במציאות העסקית של היום.
                </p>
              </article>
              <article className="vision-section">
                <h3 className="vision-title">החזון שלנו:</h3>
                <p className="vision-text">
                  קהילה של מנהיגים עסקיים בישראל שמובילים שינוי וצמיחה יחד: חשיבה חדה יותר,
                  החלטות טובות יותר, ופחות בדידות בצמרת.
                </p>
              </article>
            </div>
            <div className="values-section">
              <h3 className="values-title">הערכים שלנו:</h3>
              <div className="values-grid">
                <div className="value-item">
                  <span className="value-icon">🔒</span>
                  <h4>סודיות</h4>
                  <p>מרחב סגור שבו אפשר לדבר על מה שבאמת קורה בחברה</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">🤝</span>
                  <h4>עמיתים</h4>
                  <p>פידבק אמיתי ממנכ״לים שחווים את אותם אתגרים</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">💡</span>
                  <h4>פרקטיקה</h4>
                  <p>תובנות וכלים שאפשר ליישם כבר ביום שאחרי המפגש</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">🚀</span>
                  <h4>צמיחה</h4>
                  <p>התפתחות אישית ועסקית בסביבה ממוקדת הצלחה</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="benefits-section scroll-animate">
        <div className="container">
          <h2 className="section-title">מה תרוויחו מההצטרפות?</h2>
          <div className="benefits-grid">
            <div className="benefit-card scroll-animate-scale">
              <div className="benefit-icon">🌐</div>
              <h3>נטוורקינג ברמה הגבוהה ביותר</h3>
              <p>
                שיח אסטרטגי עם עמיתים שחווים את אותם אתגרים, נותנים פידבק אמיתי,
                ופותחים דלתות שלא נפתחות במקרה. החברים הם מנכ״לים מחברות בישראל.
              </p>
            </div>
            <div className="benefit-card scroll-animate-scale">
              <div className="benefit-icon">📚</div>
              <h3>תוכן בלעדי וכלים פרקטיים</h3>
              <p>
                גישה לתוכן ייחודי, מחקרי שוק, סדנאות מנהיגות, סיכומי מאמרים שבועיים
                והרצאות עם תובנות שאפשר לקחת ישר לחדר הישיבות.
              </p>
            </div>
            <div className="benefit-card scroll-animate-scale">
              <div className="benefit-icon">🌱</div>
              <h3>סביבה שמצמיחה מנכ״לים</h3>
              <p>
                Vistage היא לא עוד קורס ניהול — זו קהילה שמובילה שינוי וצמיחה.
                מנכ״לים שחושבים, מאתגרים ועוזרים למנכ״ל בקבוצה לראות את מה שהוא לא יכול לראות לבד.
              </p>
            </div>
            <div className="benefit-card scroll-animate-scale">
              <div className="benefit-icon">💬</div>
              <h3>שיתוף בדילמות</h3>
              <p>
                מרחב סודי לשיתוף דילמות אמיתיות, התייעצות בגובה העיניים,
                ופרספקטיבה מניסיון של מנכ״לים שכבר התמודדו עם אתגר דומה.
                מה שמאפשר בהירות והחלטות מדוייקות יותר.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="course-about-section scroll-animate">
        <div className="container">
          <h2 className="section-title">על הקבוצה</h2>

          <article className="course-ticket" aria-label="פרטי הקבוצה">
            <header className="course-ticket-head">
              <h3>קבוצת מנכ״לים</h3>
            </header>
            <div className="course-ticket-body">
              <div className="course-ticket-col">
                <span className="course-ticket-kicker">הקצב</span>
                <p className="course-ticket-primary">
                  מפגש חודשי
                  <span className="course-ticket-ltr">+ תוכן כל שבוע</span>
                </p>
                <p className="course-ticket-secondary">
                  סיכומי מאמרים · הרצאות · כלים פרקטיים
                </p>
              </div>
              <div className="course-ticket-col">
                <span className="course-ticket-kicker">Chair</span>
                <p className="course-ticket-primary">
                  ד״ר משה קטן
                </p>
              </div>
            </div>
          </article>

          <div className="course-about-stats">
            <span>40 מדינות</span>
            <span>45,000+ מנכ״לים בעולם</span>
            <span>100 מנכ״לים בישראל</span>
            <span>עד 10 מנכ״לים בקבוצה</span>
          </div>

          <blockquote className="course-about-quote">
            <p>מנכ״לים לא צריכים עוד ייעוץ מהצד — הם צריכים עמיתים בגובה העיניים.</p>
          </blockquote>

          <p className="course-about-lead">
            Vistage היא ארגון בינלאומי מוביל הפועל ב־40 מדינות, עם יותר מ־45,000 מנכ״לים
            ומנהיגים עסקיים. בישראל כבר פעילים יותר מ־100 מנכ״לים ובעלי עסקים בקבוצות שולחן עגול.
            כל קבוצה מונה עד 10 מנכ״לים מהשוק הישראלי, ומנוהלת אישית על ידי Chair בעל ניסיון רב,
            מוסמך על ידי Vistage Global.
          </p>

          <div className="course-tools">
            <div className="course-tools-purpose">
              <p className="course-tools-purpose-not">{GROUP_TOOLS.purposeNot}</p>
              <p className="course-tools-purpose-yes">{GROUP_TOOLS.purposeYes}</p>
            </div>
            <p className="course-tools-lead">{GROUP_TOOLS.lead}</p>
            <ol className="course-tools-list">
              {GROUP_TOOLS.items.map((item, index) => (
                <li
                  key={item}
                  className={`course-tools-item${index === GROUP_TOOLS.items.length - 1 ? ' course-tools-item--highlight' : ''}`}
                >
                  <span className="course-tools-index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <p>{item}</p>
                </li>
              ))}
            </ol>
            <p className="course-tools-punch">{GROUP_TOOLS.punch}</p>
          </div>

          <h3 className="course-about-subtitle">איך זה עובד</h3>
          <ol className="course-about-path">
            <li>
              <span className="course-about-path-index">01</span>
              <div className="course-about-path-body">
                <strong>שיחת היכרות</strong>
                <p>שיחה קצרה וממוקדת עם ד״ר משה קטן — לבדוק התאמה לקבוצה החדשה</p>
              </div>
            </li>
            <li>
              <span className="course-about-path-index">02</span>
              <div className="course-about-path-body">
                <strong>מפגש ניסיון</strong>
                <p>בדיקת התאמה הדדית בין המנכ״ל לקבוצה</p>
              </div>
            </li>
            <li>
              <span className="course-about-path-index">03</span>
              <div className="course-about-path-body">
                <strong>הסכם הצטרפות ל־Vistage ותנאי החברות</strong>
                <p>דמי חברות, תקופת החברות ותנאי תשלום</p>
              </div>
            </li>
            <li>
              <span className="course-about-path-index">04</span>
              <div className="course-about-path-body">
                <strong>חבר בקבוצה</strong>
                <p>מפגשים חודשיים, שיתוף והתייעצות, ליווי אישי, שיתופי פעולה</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section className="vistage-world scroll-animate">
        <div className="container">
          <h2 className="section-title">Vistage בעולם ובישראל</h2>
          <div className="vistage-world-nums">
            <div>
              <b>1957</b>
              <span>שנת ייסוד</span>
            </div>
            <div>
              <b>40</b>
              <span>מדינות</span>
            </div>
            <div>
              <b>45,000+</b>
              <span>מנכ״לים בעולם</span>
            </div>
            <div>
              <b>100</b>
              <span>מנכ״לים בישראל</span>
            </div>
          </div>
          <p className="experience-description">
            Vistage פועלת מאז 1957 ב־40 מדינות, עם יותר מ־45,000 מנכ״לים ומנהיגים עסקיים.
            בישראל כבר פעילים יותר מ־100 מנכ״לים ובעלי עסקים בקבוצות שולחן עגול —
            וד״ר משה קטן מקים כעת קבוצה חדשה.
          </p>
        </div>
      </section>

      <section id="videos" className="experience-section vistage-logos scroll-animate">
        <div className="container">
          <div className="partner-companies-section">
            <h3 className="video-section-title">חברות שנמצאות בקבוצה</h3>
            {groupCompanies.length === 0 ? (
              <p className="video-empty-hint">הלוגואים ייטענו כאן ברגע שהמדיה תהיה זמינה</p>
            ) : (
              <LogoCarousel companies={groupCompanies} />
            )}
          </div>
        </div>
      </section>

      <section className="experience-section scroll-animate">
        <div className="container">
          <h2 className="section-title">למה מנכ״לים מצטרפים</h2>
          <div className="feedback-grid">
            <div className="feedback-card scroll-animate-left">
              <p className="feedback-text">
                ״אם אתם מתמודדים עם אתגרים עסקיים מורכבים ומחפשים סביבה איכותית להתפתחות מקצועית — זו ההזמנה.״
              </p>
            </div>
            <div className="feedback-card scroll-animate">
              <p className="feedback-text">
                ״לא עוד רשת כללית. כאן נפגשים מנכ״לים שמדברים אותה שפה, חולקים דילמות אמיתיות, ומקבלים פידבק מהשטח.״
              </p>
            </div>
            <div className="feedback-card scroll-animate-right">
              <p className="feedback-text">
                ״Vistage היא לא עוד קורס ניהול — זו קהילה של מנהיגים עסקיים שמובילים שינוי וצמיחה.״
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="instructor-section scroll-animate">
        <div className="container">
          <div className="instructor-content">
            <div className="instructor-image">
              <div className="instructor-portrait">
                <OptionalImg src={moshePhoto} alt="ד״ר משה קטן" className="dr-moshe-photo" />
              </div>
            </div>
            <div className="instructor-info">
              <h3>ד״ר משה קטן</h3>
              <div className="instructor-content-wrapper">
                <div className="certificate-section">
                  <div className="certificate-container">
                    <OptionalImg
                      src={phdCertificate}
                      alt="תעודת דוקטורט מאוניברסיטת פוזנן"
                      className="certificate-image"
                    />
                  </div>
                </div>
                <div className="instructor-text-content">
                  <p className="instructor-description">
                    אחרי שביצע שורה של תפקידים בכירים והנהיג צוותים גדולים בחברות בינלאומיות
                    הפועלות בישראל כמו מנכ״ל קרלסברג, סגן נשיא קבוצת קוקה־קולה ושטראוס עלית,
                    ד״ר קטן נמנה היום עם מס׳ מצומצם של מומחים לפיתוח מנהיגות עסקית מעשית.
                    זאת באמצעות גילוי ה־C.O.D לפיתוח מנהיגות במסגרת לימודי הדוקטורט שלו ב־University of Poznan.
                    בנוסף הוא Chair ב־Vistage ומוביל קבוצת מנכ״לים בישראל.
                    הוא גם מרצה בקורסי פיתוח מנהיגות בפקולטה לניהול באוניברסיטת תל אביב,
                    לסטודנטים לתואר ראשון ושני.
                  </p>
                  <p className="instructor-description">
                    שיטת ה־C.O.D התגלתה במהלך המחקר המעמיק שביצע עם מנהלים בכירים בחברות גלובליות.
                    התהליך מאפשר כיום לאירגונים רבים לשפר משמעותית את תפוקות הארגון ולהתמודד מול
                    שינויים מתכוננים ושינויים בלתי צפויים.
                  </p>
                  <p className="instructor-description">
                    מוכנים לבדוק הצטרפות לקבוצה החדשה שאני מקים? אני מזמין אתכם לשיחת היכרות
                    קצרה וממוקדת לבחינת התאמה הדדית.
                  </p>
                </div>
              </div>
              <div className="instructor-achievements">
                <div className="achievement">
                  <span className="achievement-icon">🏆</span>
                  <span>מומחה לפיתוח מנהיגות עסקית מעשית</span>
                </div>
                <div className="achievement">
                  <span className="achievement-icon">🎯</span>
                  <span>30 שנות ניסיון בחברות בינלאומיות</span>
                </div>
                <div className="achievement">
                  <span className="achievement-icon">💼</span>
                  <span>Chair ב-Vistage</span>
                </div>
              </div>
              <div className="instructor-logos">
                <div className="tau-logos">
                  <div className="tau-logos-grid">
                    {TAU_LOGOS.map((logo) => (
                      <OptionalImg
                        key={logo.alt}
                        wrapClassName="tau-logo-item"
                        src={logo.src}
                        alt={logo.alt}
                        className="tau-logo"
                      />
                    ))}
                  </div>
                </div>
                <div className="logos-grid">
                  {INSTRUCTOR_LOGOS.map((logo) => (
                    <OptionalImg
                      key={logo.alt}
                      wrapClassName="logo-item"
                      src={logo.src}
                      alt={logo.alt}
                      className="logo-image"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactForms />

      <footer className="footer scroll-animate">
        <div className="container">
          <p>אל תפספסו את ההזדמנות להצטרף לשולחן העגול של Vistage Israel עם ד״ר משה קטן</p>
          <p className="footer-note">
            054-9003388 · Moshe.Katan@Vistage.co.il · vistage.co.il
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
